/**
 * CompactDateRangeBar Component (Phase 5 — UX Overhaul)
 *
 * Single-row compact bar for date range selection displayed above
 * the preview panel. Replaces the full-page DateRangeSelector step
 * to combine date range + preview into one screen.
 */

import { useState } from "react";
import { CalendarBlank } from "@phosphor-icons/react";
import {
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Switch,
} from "@provenleap/ui";
import { DATE_RANGE_PRESETS } from "../../lib/dateRangeUtils";
import type { DateRangePresetKey } from "../../lib/dateRangeUtils";
import type { DateRangeState } from "../../hooks/useDateRange";

interface CompactDateRangeBarProps {
  state: DateRangeState;
  onSelectPreset: (key: DateRangePresetKey) => void;
  onToggleComparison: () => void;
  validationError?: string | null;
  className?: string;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00Z");
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

const DEFAULT_CLASS = "sticky top-0 z-10 flex items-center gap-3 rounded-lg border bg-card px-4 py-2.5 shadow-sm mb-4";

export function CompactDateRangeBar({
  state,
  onSelectPreset,
  onToggleComparison,
  validationError,
  className,
}: CompactDateRangeBarProps) {
  const [presetOpen, setPresetOpen] = useState(false);

  const currentPreset = DATE_RANGE_PRESETS.find(
    (p) => p.key === state.presetKey
  );
  const presetLabel = currentPreset?.label ?? "Select period";
  const dateDisplay =
    state.start && state.end
      ? `${formatDate(state.start)} – ${formatDate(state.end)}`
      : "";

  return (
    <div
      className={className ?? DEFAULT_CLASS}
      data-testid="compact-date-range-bar"
    >
      <CalendarBlank size={18} className="shrink-0 text-muted-foreground" />

      {/* Preset dropdown */}
      <Popover open={presetOpen} onOpenChange={setPresetOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="min-w-[140px]"
            data-testid="preset-trigger"
          >
            {presetLabel}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-1" align="start">
          {DATE_RANGE_PRESETS.filter((p) => p.key !== "custom").map(
            (preset) => (
              <button
                key={preset.key}
                className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors ${
                  state.presetKey === preset.key
                    ? "bg-primary/10 text-primary font-medium"
                    : "hover:bg-accent text-foreground"
                }`}
                onClick={() => {
                  onSelectPreset(preset.key);
                  setPresetOpen(false);
                }}
                data-testid={`preset-${preset.key}`}
              >
                {preset.label}
              </button>
            )
          )}
        </PopoverContent>
      </Popover>

      {/* Date range display */}
      {dateDisplay && (
        <span
          className="text-sm text-muted-foreground"
          data-testid="date-display"
        >
          {dateDisplay}
        </span>
      )}

      {/* Comparison toggle */}
      <div className="ml-auto flex items-center gap-2">
        <label
          htmlFor="compact-comparison-toggle"
          className="text-xs text-muted-foreground cursor-pointer"
        >
          Compare
        </label>
        <Switch
          id="compact-comparison-toggle"
          checked={state.comparisonEnabled}
          onCheckedChange={onToggleComparison}
          data-testid="comparison-toggle"
        />
      </div>

      {/* Validation error */}
      {validationError && (
        <span
          className="text-xs text-red-600 ml-2"
          data-testid="date-validation-error"
        >
          {validationError}
        </span>
      )}
    </div>
  );
}
